
import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface LeaderboardFiltersProps {
  onImportClick: () => void;
  filterType: "product" | "industry";
  setFilterType: (value: "product" | "industry") => void;
  selectedRegion: string;
  setSelectedRegion: (value: string) => void;
  regions: string[];
  selectedCountry: string;
  setSelectedCountry: (value: string) => void;
  countryOptions: string[];
  selectedAccountName: string;
  setSelectedAccountName: (value: string) => void;
  accountNameOptions: string[];
  selectedProductLine: string;
  setSelectedProductLine: (value: string) => void;
  productLines: string[];
  selectedIndustry: string;
  setSelectedIndustry: (value: string) => void;
  industries: string[];
}

const LeaderboardFilters = ({
  onImportClick,
  filterType,
  setFilterType,
  selectedRegion,
  setSelectedRegion,
  regions,
  selectedCountry,
  setSelectedCountry,
  countryOptions,
  selectedAccountName,
  setSelectedAccountName,
  accountNameOptions,
  selectedProductLine,
  setSelectedProductLine,
  productLines,
  selectedIndustry,
  setSelectedIndustry,
  industries
}: LeaderboardFiltersProps) => {
  return (
    <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6">
      <h2 className="font-bold text-lg">YTD Achievement</h2>
      
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onImportClick} className="bg-primary text-black border-slate-700">
            <Download className="h-4 w-4 mr-2" />
            Import Excel Data
          </Button>
          
          <span className="text-sm text-slate-400">Filter by:</span>
          
          <Select onValueChange={setSelectedRegion} value={selectedRegion}>
            <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
              <SelectValue placeholder="Select Region" />
            </SelectTrigger>
            <SelectContent>
              {regions.map(region => <SelectItem key={region} value={region}>{region}</SelectItem>)}
            </SelectContent>
          </Select>
          
          <Select onValueChange={setSelectedCountry} value={selectedCountry}>
            <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {countryOptions.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
            </SelectContent>
          </Select>
          
          <Select onValueChange={setSelectedAccountName} value={selectedAccountName}>
            <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
              <SelectValue placeholder="Select Account" />
            </SelectTrigger>
            <SelectContent>
              {accountNameOptions.map(accountName => <SelectItem key={accountName} value={accountName}>{accountName}</SelectItem>)}
            </SelectContent>
          </Select>
          
          <RadioGroup className="flex flex-col items-start space-y-0 text-[10px]" defaultValue="product" value={filterType} onValueChange={value => setFilterType(value as "product" | "industry")}>
            <div className="flex items-center space-x-1">
              <RadioGroupItem value="product" id="product" className="text-primary" />
              <Label htmlFor="product" className="cursor-pointer text-white space-y-0 text-[10px]">Product</Label>
            </div>
            <div className="flex items-center space-x-1">
              <RadioGroupItem value="industry" id="industry" className="text-primary" />
              <Label htmlFor="industry" className="cursor-pointer text-white space-y-0 text-[10px]">Industry</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="flex flex-wrap gap-0">
          {filterType === "product" ? (
            <Select onValueChange={setSelectedProductLine} value={selectedProductLine}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
                <SelectValue placeholder="Select Product Line" />
              </SelectTrigger>
              <SelectContent>
                {productLines.map(productLine => <SelectItem key={productLine} value={productLine}>{productLine}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Select onValueChange={setSelectedIndustry} value={selectedIndustry}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
                <SelectValue placeholder="Select Industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map(industry => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardFilters;
